import { useEffect, useState } from 'react';
import { type UserInfo } from '@monorepo-hol/providers';

import type { PdfData } from '../../../types/documents';
import {
  type LangStateType,
  useLanguageContext,
} from '../../../provider/LanguageProvider';
import { useGetDocument2 } from '../../../modules/document';
import type { DataDocumentDetail } from '../services/document-detail/types';
import { MainCatalog } from '../../../types/catalog';

export interface UseDocumentDataProps {
  guid?: string;
  docId?: string;
  user?: UserInfo;
  data?: DataDocumentDetail;
  docData?: MainCatalog;
  authorized?: boolean;
}

export interface IUseDocumentData {
  isDocumentNotFound: boolean;
  isDocumentReady: boolean;
  isDocumentError: boolean;
  pdfDoc: PdfData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  smartDocumentContent: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pdfDocList: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lampiranList?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mainDocument: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mainDocumentXml?: any;
  LangState: LangStateType;
  is404: boolean;
}

export function useDocumentData({
  guid,
  docData,
  user,
  authorized,
}: UseDocumentDataProps): IUseDocumentData {
  const [isDocumentReady, setIsDocumentReady] = useState(false);
  const [isDocumentNotFound, setIsDocumentNotFound] = useState(false);
  const [isDocumentError, setIsDocumentError] = useState(false);
  const [pdfDoc, setPdfDoc] = useState<PdfData>(null);
  const [pdfDocList, setPdfDocList] = useState([]);
  const { LangState } = useLanguageContext();

  const {
    mainDocument: {
      documentMetadataStatus,
      is404,
      isPdfError,
      isPdfLoading,
      mainDocument,
      pdfData,
      pdfStatus,
    },
    smartDoc: { smartDocumentContent, smartDocStatus },
  } = useGetDocument2({
    guid,
    docId: docData?.pdfDocumentGuid,
    xmlId: docData?.xmlDocumentGuid,
    mainDocument: docData?.mainDocument,
    jwt: user?.jwt || '',
    type: 'main',
    authorized: authorized,
  });

  useEffect(() => {
    setPdfDocList(docData?.documentList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (smartDocStatus === 'idle' && pdfStatus === 'success') ||
      (smartDocStatus === 'success' && pdfStatus === 'success')
    ) {
      setIsDocumentReady(true);
      setPdfDoc(pdfData);
    } else if (
      (smartDocStatus === 'error' || smartDocStatus === 'idle') &&
      pdfStatus === 'error'
    ) {
      setIsDocumentReady(true);
      setIsDocumentError(true);
    } else if (
      documentMetadataStatus === 'success' &&
      smartDocStatus === 'idle' &&
      pdfStatus === 'idle'
    ) {
      if (pdfData.type && !pdfData.blob) {
        setIsDocumentReady(true);
        setPdfDoc(pdfData);
      } else {
        setIsDocumentNotFound(true);
      }
    } else if (smartDocStatus === 'loading' || pdfStatus === 'loading') {
      setIsDocumentReady(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfStatus, smartDocStatus, isPdfLoading, isPdfError]);

  return {
    isDocumentNotFound,
    isDocumentReady,
    pdfDoc,
    smartDocumentContent,
    pdfDocList,
    mainDocument,
    LangState,
    isDocumentError,
    is404,
  };
}
